
import userAgent from '../utils/userAgent';

const preferredVoices = {
  en: {name: 'Google US English', rate: 0.8 },
  en_US: {name: 'inglés Estados Unidos', rate: 0.8 },
  es: {name: 'Google español', rate: 0.8 },
  es_ES: {name: 'Google español', rate: 0.8 },
  es_MX: {name: 'español México', rate: 0.8 },
  it: {name: 'Luca', rate: 0.8 },
  fr: {name: 'Amelie', rate: 0.6 },
  pt: {name: 'Luciana', rate: 0.6 },
  pl: {name: 'Zosia', rate: 0.6 },
  ro: {name: 'Zosia', rate: 0.6 },
  de: {name: 'Google Deutsch', rate: 0.8 }
};

const otherSpeeds = [.25, .5, .85, 1.05, 1.15];
const androidSpeeds = [.1, .5, 1, 1.2, 1.5];

export default class LocalSynthesis {
  constructor(language, locale){
    this.init(language, locale);
  }

  init(language, locale){
    this.language = language;
    this.locale = locale;
    this._findPreferredVoice();
  }

  setSpeed(speed){
    this.speechRate = this._transformSpeedToRate(speed);
  }

  setVoice(voice){
    this.voice = voice;
  }

  async getVoices(){
    return await this.voices; 
  }

  async getVoice(){
    return this.voice || (await this.voices)[0];
  }

  cancel(){
    window.utterances = [];
    window.speechSynthesis.cancel();
  }

  async play(text){
    this.cancel();

    await this._safariBugWorkaround();
    
    return new Promise(res => {
      const utterance = this._createUtterance(text);
      utterance.onend = res;
      window.speechSynthesis.speak(utterance);
    })
  }

  prepareTexts(){
    // No implementation required in local strategy
  }

  _createUtterance(text){
    const utter = this._initUtterance(text);
    window.utterances.push(utter);
    return utter;
  }

  _initUtterance(text){
    let utter = new SpeechSynthesisUtterance(text.toLowerCase());
    utter.voice = this.voice;
    utter.rate = this.speechRate || 1;
    utter.lang = utter.voice.lang;
    return utter;
  }

  async _findPreferredVoice(){
    this.voices = LocalSynthesis.getVoices(this.language, this.locale);
    this.voice = (await this.voices)[0]
  }

  _transformSpeedToRate(speed){
    if (userAgent.platform === 'android'){
      return androidSpeeds[speed -1];
    } else {
      return otherSpeeds[speed -1 ];
    }
  }

  async _safariBugWorkaround(){
    if (!window.speechSynthesis.speaking || (userAgent.browser !== 'safari' && userAgent.platform !== 'electron'))
      return;

    return new Promise((res)=>{
      setTimeout(()=>{res()}, 250);
    })
  }
  
  static async getVoices(language, locale){
    if (!window.speechSynthesis){
      return [];
    }
    const voices = await LocalSynthesis.getBrowserVoices();
    
    const filteredByLanguage = voices.filter(obj => {
      let langMatch = obj.lang.split(/-|_/)[0].localeCompare(language) === 0;
      let cond = (userAgent.browser === 'chrome' && userAgent.platform !== 'android' && userAgent.platform !== 'electron') ? (langMatch && obj.name.indexOf('Google') > -1) : langMatch;
      return cond;
    });

    // order voices by relevance
    const prefferedVoice = (preferredVoices[language] ?? {}).name
    filteredByLanguage.sort((voice1, voice2)=>{
      // 2 points if it matches locale
      let value1 = voice1.lang === locale ? 2 : 0;
      let value2 = voice2.lang === locale ? 2 : 0;
      // 1 points if it is in preferredVoices
      value1 += voice1.name.localeCompare(prefferedVoice) === 0 ? 1 : 0;
      value2 += voice2.name.localeCompare(prefferedVoice) === 0 ? 1 : 0;

      return value2 - value1;
    })

    return filteredByLanguage;
  }

  static getBrowserVoices(){
    return new Promise(res => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0){
        res(voices)
      } else {
        const timeoutTimer = 2000;
        const intervalId =  setInterval(() => {
          const voices = window.speechSynthesis.getVoices();
          if (voices.length > 0){
            res(voices);
            clearInterval(intervalId);
          }
        }, 50);
        setTimeout(()=>{
          clearInterval(intervalId);
          res([]);
        }, timeoutTimer);
      }
    })
  }

  static async isSupported(language){
    return (await LocalSynthesis.getVoices(language)).length > 0;
  }
}
